import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';

import type { DesignData, MediaElement, MediaImage, MediaText } from 'editor/src/store/design/types';

import getLayoutFrames from 'editor/src/util/reflectDesignData/getLayoutFrames';
import { ReflectContext } from 'editor/src/util/reflectDesignData/getReflectContext';
import reflectMediaElement from 'editor/src/util/reflectDesignData/reflectMediaElement';
import { getMediaBoxes } from 'editor/src/util/reflectDesignData/reflectMediaElements';
import updateImageElement from 'editor/src/util/reflectDesignData/updateImageElement';

import getSpreadWidthFromSpread from '../selector/getSpreadWidthFromSpread';

import { getTextFlowChains, getTextFlowChainText, setTextFlowChainText } from './textFlow';
import updateTextPropertiesWithoutRender from './updateTextPropertiesWithoutRender';

const calendarFrontPostfix = ' front';

// Copies of a slot template share its name, so a name alone would pair the first copy with every
// frame's value. A layer in a frame pairs with the same frame's layer in the next design.
function sameSlotFrame(a: MediaElement, b: MediaElement): boolean {
  return a.slotFrame?.layoutId === b.slotFrame?.layoutId && a.slotFrame?.frameId === b.slotFrame?.frameId;
}

/**
 * A chain of linked frames carries its whole text into the next design's chain, which the flow pours
 * over that chain's frames. Copied frame by frame, the slices would be joined back with the next
 * design's joints, putting a paragraph break wherever the previous frames were cut.
 */
function carryTextFlowChains(previousDesign: DesignData, newDesign: DesignData, carried: Map<MediaText, MediaText>) {
  const nextChains = getTextFlowChains(newDesign).filter((chain) => chain.length > 1);
  getTextFlowChains(previousDesign).forEach((chain) => {
    if (chain.length < 2) {
      return;
    }
    const nextFrames = new Set(chain.map(({ element }) => carried.get(element)));
    const nextChain = nextChains.find((members) => members.some(({ element }) => nextFrames.has(element)));
    if (!nextChain) {
      return;
    }
    setTextFlowChainText(nextChain, getTextFlowChainText(chain)).forEach(({ address, update }) => {
      const element = newDesign.spreads[address.spreadIndex].pages[address.pageIndex].groups.media?.[
        address.elementIndex
      ] as MediaText;
      element.extra = update.extra as MediaText['extra'];
    });
  });
}

function applyDesign(
  previousDesign: DesignData,
  previousOriginalDesign: DesignData | undefined,
  nextDesign: DesignData,
  allowNew: boolean,
  reflectContext: ReflectContext | undefined,
): DesignData {
  const newDesign = cloneDeep(nextDesign);
  const carriedTexts = new Map<MediaText, MediaText>();
  let resetLayouts = false;
  previousDesign.spreads.forEach((previousSpread, sourceSpreadIndex) => {
    const nextSpread = newDesign.spreads.find((spread) => {
      const matchSpread = previousSpread.name === spread.name;
      if (matchSpread) {
        return true;
      }
      if (previousDesign.calendar) {
        const matchCalendarSpread =
          previousSpread.name.toLowerCase().replace(calendarFrontPostfix, '') ===
          spread.name.toLowerCase().replace(calendarFrontPostfix, '');
        if (matchCalendarSpread) {
          resetLayouts = true;
          return true;
        }
      }
      return false;
    });
    const previousOriginalSpread = previousOriginalDesign?.spreads[sourceSpreadIndex];
    const sourcePageIndex = previousSpread.pages.findIndex(
      // it's possible to have 'blank' + media on photobook front-inner spread
      (page) => (!!page.groups.mediabox?.[0] || !!page.groups.blank?.[0]) && !!page.groups.media,
    );
    const previousOriginalPage = previousOriginalSpread?.pages[sourcePageIndex];
    const sourcePage = previousSpread.pages[sourcePageIndex];
    if (!sourcePage) {
      return;
    }

    const destPage = nextSpread?.pages[sourcePageIndex];
    if (!nextSpread || !destPage) {
      return;
    }
    let nextMedia = destPage.groups.media ?? [];

    // reset spread if the layout is different from default
    const layoutChanged = [nextSpread.layoutSchemaName, previousOriginalSpread?.layoutSchemaName].includes(
      previousSpread?.layoutSchemaName,
    );
    if (!layoutChanged && !resetLayouts) {
      nextSpread.layoutSchemaName = previousSpread.layoutSchemaName;
      nextMedia = [];
    }

    // If the old design has the same conditions as the new design, copy only the activeOptionId from the old design to preserve the selected option when the variant is changed.
    if (
      previousSpread.conditionGroup?.conditions &&
      Object.keys(previousSpread.conditionGroup.conditions).length > 0 &&
      nextSpread?.conditionGroup &&
      Object.keys(nextSpread.conditionGroup.conditions).length > 0
    ) {
      const oldConditions = previousSpread.conditionGroup.conditions;
      const newConditions = nextSpread.conditionGroup.conditions;

      Object.keys(newConditions).forEach((key) => {
        if (oldConditions[key]?.id === newConditions[key]?.id) {
          newConditions[key].activeOptionId = oldConditions[key].activeOptionId;
        }
      });
    }

    destPage.groups.media = nextMedia;
    const elementNames = new Set<string>(nextMedia.map((element) => element.name));

    // used to reflect media elements
    const mediaBoxes = getMediaBoxes(previousSpread, nextSpread);
    const spreadGroup = previousDesign.spread_groups?.find((spreadGroup) =>
      spreadGroup.spreadIndexes.includes(sourceSpreadIndex),
    );
    const sourceLayoutFrames = getLayoutFrames(
      previousSpread,
      reflectContext?.layouts ?? [],
      previousDesign.spreads,
      spreadGroup,
    );
    const destLayoutFrames = getLayoutFrames(nextSpread, reflectContext?.layouts ?? [], newDesign.spreads, spreadGroup);

    const destSpreadWidth = getSpreadWidthFromSpread(nextSpread);
    const mediaContext =
      reflectContext?.fonts && reflectContext?.gridDesigns
        ? {
            fonts: reflectContext.fonts,
            gridDesigns: reflectContext.gridDesigns,
          }
        : undefined;

    previousSpread.pages[sourcePageIndex].groups.media?.forEach((element, index) => {
      if (allowNew && element.createdWhileEmbedded && mediaBoxes && !resetLayouts) {
        const newElement = reflectMediaElement(
          mediaBoxes.sourceMediaBox,
          mediaBoxes.destMediaBox,
          sourceLayoutFrames,
          destLayoutFrames,
          previousSpread,
          nextSpread,
          destSpreadWidth,
          element,
          'adapt',
          mediaContext,
          newDesign.calendar,
        );
        // The same design sent again already holds this buyer element. The live copy replaces it,
        // so the uuid stays unique and the buyer's edits since the load are kept.
        const sameElementIndex = nextMedia.findIndex(
          (el) => el.createdWhileEmbedded && el.uuid === element.uuid && el.type === element.type,
        );
        if (sameElementIndex !== -1) {
          nextMedia[sameElementIndex] = newElement;
          return;
        }
        if (elementNames.has(newElement.name)) {
          newElement.name += ` ${uuidv4()}`;
          elementNames.add(newElement.name);
        }
        nextMedia.push(newElement);
        return;
      }

      if (element.personalizationLocked) {
        return;
      }

      let nextElement = nextMedia.find((el) => {
        if (previousDesign.calendar && (element as MediaImage).imageId) {
          const marchImage = resetLayouts || (el as MediaImage).layoutFrameId === (element as MediaImage).layoutFrameId;
          return !el.personalizationLocked && marchImage && el.type === element.type;
        }
        return (
          !el.personalizationLocked &&
          el.name === element.name &&
          el.type === element.type &&
          sameSlotFrame(el, element)
        );
      });

      const originalElement =
        !nextElement &&
        previousOriginalPage?.groups.media?.find(
          (originalElement) => originalElement.name === element.name && sameSlotFrame(originalElement, element),
        );

      // look for elements with the same initial values
      if (originalElement) {
        nextElement = nextMedia.find(
          (el) =>
            !el.personalizationLocked &&
            sameSlotFrame(el, element) &&
            ((el.type === 'text' && el.type === originalElement.type && el.extra.text === originalElement.extra.text) ||
              (el.type === 'image' && el.type === originalElement.type && el.imageId === originalElement.imageId)),
        );
      }

      if (nextElement) {
        if (element.type === 'image') {
          const imageUpdated = (previousOriginalPage?.groups.media?.[index] as MediaImage)?.imageId !== element.imageId;
          const nextImage = nextElement as MediaImage;
          if (imageUpdated) {
            nextImage.imageId = element.imageId;
            nextImage.sample = element.sample;
          }
          updateImageElement(element, nextImage, 'adapt', true);
        } else if (element.type === 'text') {
          const nextText = nextElement as MediaText;
          nextText.extra.text = element.extra.text;
          nextText.sample = element.sample;
          updateTextPropertiesWithoutRender(nextText, nextText, undefined);
          carriedTexts.set(element, nextText);
        }
      }
    });
  });

  carryTextFlowChains(previousDesign, newDesign, carriedTexts);

  // Texts carried over keep their style references, so the styles the next design lacks come along.
  const nextStyleIds = new Set(newDesign.textStyles?.map(({ id }) => id));
  const referencedStyleIds = new Set(
    newDesign.spreads.flatMap((spread) =>
      spread.pages.flatMap((page) =>
        (page.groups.media ?? []).map((element) => (element.type === 'text' ? element.extra.textStyleId : undefined)),
      ),
    ),
  );
  const missingStyles = (previousDesign.textStyles ?? []).filter(
    ({ id }) => referencedStyleIds.has(id) && !nextStyleIds.has(id),
  );
  if (missingStyles.length) {
    newDesign.textStyles = [...(newDesign.textStyles ?? []), ...cloneDeep(missingStyles)];
  }
  return newDesign;
}

export default applyDesign;
